import React, { ComponentProps, useEffect, useMemo, useState } from 'react';
import mustache from 'mustache';
import clsx from 'clsx';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarClickTypes,
  NarvarClickSubTypes,
  NarvarEventComponents,
} from '../../../hooks/Analytics';
import { useTranslations } from '../../../hooks/useTranslations';
import SharingTruckIcon from '../../ui/icons/SharingTruck/SharingTruck';
import Alert from '../../ui/Alert/Alert';
import TextLink from '../../ui/TextLink/TextLink';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import { LayoutTree } from '../DynamicLayout';
import { Devices } from '../DynamicLayout/layoutTree';
import SharingOptions from './components/SharingOptions';
import { useTheme } from '../../../hooks/useTheme';
import styles from './GiftTracking.module.css';

export interface GiftTrackingProps {
  featureName?: string;
  userAgentDevice?: LayoutTree.Devices;
}

const renderOptions = { escape: (x: string): string => x };

export const GiftTracking = ({
  featureName,
  userAgentDevice,
}: GiftTrackingProps) => {
  const [isVisible, setIsVisible] = useState(true);
  const [showSharingOptions, setShowSharingOptions] = useState(false);

  const logEvent = useAnalyticsPost();

  const {
    bodytextDefaultNone,
    linksNoneDefault: giftTrackingLinkStyle,
    linksNoneHover: giftTrackingLinkHoverStyle,
  } = useTheme();

  const {
    state: { requestUrl, experienceHost, embedReferrerUrl },
  } = usePageContext();

  const {
    giftTrackingBannerMessage,
    giftTrackingBannerLink,
    giftTrackingSharingMessageSubject,
    giftTrackingSharingMessageBody,
  } = useTranslations();

  const giftTrackingMessageStyle = bodytextDefaultNone && {
    ...bodytextDefaultNone,
    fontWeight: 'bold',
  };

  const isMobileDevice = userAgentDevice === Devices.MOBILE;

  // Values need to be memoized to prevent `logEvent` useEffect from
  // re-running on every render (eslint/react-hooks/exhaustive-deps)
  const baseLogInfo = useMemo(
    () => ({
      eventComponent: NarvarEventComponents.NTH_PACK_GIFT_TRACKING,
      eventFeature: NarvarEventComponents.NTH_PACK_GIFT_TRACKING,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_PACK_GIFT_TRACKING,
    }),
    [featureName],
  );

  const handleDismissClicked = (): void => {
    setIsVisible(false);

    logEvent({
      ...baseLogInfo,
      clickCategory: NarvarClickSubTypes.DISMISS,
      clickSubType: NarvarClickSubTypes.DISMISS,
      clickType: NarvarClickTypes.INTERACTION,
      eventType: NarvarEventTypes.CLICK,
    });
  };

  useEffect(() => {
    logEvent({
      ...baseLogInfo,
      eventType: NarvarEventTypes.COMPONENT_RENDER,
    });
  }, [logEvent, baseLogInfo]);

  // Appends cateogry=sharing more safely than manually appending
  // `&category=sharing` to `requestUrl`
  const giftTrackingURL = new URL(
    experienceHost === 'EMBED' && embedReferrerUrl
      ? embedReferrerUrl
      : requestUrl || window.location.href,
  );
  giftTrackingURL.searchParams.set('category', 'sharing');
  const giftTrackingLink = giftTrackingURL.href;

  const giftTrackingSharingMessageBodyMustachedDesktop = mustache.render(
    giftTrackingSharingMessageBody || '{{giftTrackingUrl}}',
    {
      giftTrackingUrl: encodeURIComponent(giftTrackingLink),
    },
    undefined,
    renderOptions,
  );

  const giftTrackingSharingMessageBodyMustachedMobile = mustache.render(
    giftTrackingSharingMessageBody || '{{giftTrackingUrl}}',
    {
      giftTrackingUrl: giftTrackingLink,
    },
    undefined,
    renderOptions,
  );

  const handleShowSharingOptions = () => {
    logEvent({
      clickCategory: isMobileDevice
        ? NarvarClickSubTypes.SHARE_LINK
        : NarvarClickSubTypes.SHOW_SHARING_OPTIONS,
      clickSubType: NarvarClickSubTypes.GIFT_TRACKING,
      clickType: NarvarClickTypes.INTERACTION,
      eventComponent: NarvarEventComponents.NTH_PACK_GIFT_TRACKING,
      eventFeature: NarvarEventComponents.NTH_PACK_GIFT_TRACKING,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_PACK_GIFT_TRACKING,
    });

    if (isMobileDevice && window.navigator.share) {
      window.navigator
        .share({
          title: giftTrackingSharingMessageSubject || '',
          text: giftTrackingSharingMessageBodyMustachedMobile || '',
        })
        .catch((error) => {
          // eslint-disable-next-line no-console
          console.error(error);
        });
    }
    if (!isMobileDevice) setShowSharingOptions(true);
  };

  const classes = clsx({
    [styles.landscapeContainer]: true,
    [styles.showSharingOptions]: showSharingOptions,
  });

  return (
    <Alert
      show={isVisible}
      dismissible
      dismissOnClick={handleDismissClicked}
      data-test="giftTrackingFeature"
    >
      <div className={classes} data-testid="gift-tracking-view">
        <div className={styles.sharingTruckContainer}>
          <SharingTruckIcon />
        </div>
        <div className={styles.bannerContainer}>
          {giftTrackingBannerMessage && (
            <div style={giftTrackingMessageStyle}>
              {giftTrackingBannerMessage}
            </div>
          )}
          {giftTrackingBannerLink && (
            <TextLink
              linkStyle={{
                sDefault: giftTrackingLinkStyle,
                sHover: giftTrackingLinkHoverStyle,
              }}
              onClick={handleShowSharingOptions}
              aria-expanded={showSharingOptions}
              aria-controls="sharing-options"
            >
              {giftTrackingBannerLink}
            </TextLink>
          )}
        </div>
      </div>
      <div id="sharing-options" hidden={!showSharingOptions}>
        <SharingOptions
          featureName={featureName}
          giftTrackingSharingMessageSubject={
            giftTrackingSharingMessageSubject || ''
          }
          giftTrackingSharingMessageBody={
            isMobileDevice
              ? giftTrackingSharingMessageBodyMustachedMobile
              : giftTrackingSharingMessageBodyMustachedDesktop
          }
          giftTrackingLink={giftTrackingLink}
        />
      </div>
    </Alert>
  );
};

export default GiftTracking;
